import { useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { useState, type FormEvent, type JSX } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
  PasswordInput,
  Spinner,
} from "@mailflare/ui";

import { queryKeys } from "@/lib/query-keys";
import { routes } from "@/features/mailboxes/routes";

import { useLogin } from "./hooks";
import { authenticateWithPasskey, isWebAuthnAvailable } from "./passkey";
import { AuthCard, errorText } from "./components/auth-card";

interface LoginLocationState {
  readonly from?: string;
  readonly challengeId?: string;
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const state = location.state as LoginLocationState | null;
  const destination = state?.from ?? routes.root;

  const login = useLogin();
  const passkey = useMutation({
    mutationFn: () => authenticateWithPasskey(email.length > 0 ? email : undefined),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.session });
      navigate(destination, { replace: true });
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    login.mutate(
      { email, password },
      {
        onSuccess: (result) => {
          if (result.status === "authenticated") {
            navigate(destination, { replace: true });
          } else {
            navigate(routes.loginTotp, {
              state: { challengeId: result.challengeId, from: destination },
            });
          }
        },
      },
    );
  };

  const error = login.error ?? passkey.error;

  return (
    <AuthCard
      title="Sign in"
      description="Use your email and password, or a passkey."
      footer={
        <p className="text-muted-foreground">
          No account?{" "}
          <Link className="text-primary underline" to={routes.signup}>
            Create one
          </Link>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="space-y-1.5">
          <Label htmlFor="login-email">Email</Label>
          <Input
            id="login-email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="login-password">Password</Label>
          <PasswordInput
            id="login-password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        {error !== null ? (
          <Alert variant="destructive">
            <AlertDescription>{errorText(error)}</AlertDescription>
          </Alert>
        ) : null}

        <Button
          type="submit"
          className="w-full"
          disabled={login.isPending}
          data-testid="login-submit"
        >
          {login.isPending ? <Spinner aria-hidden className="size-4" /> : null}
          Sign in
        </Button>
      </form>

      {isWebAuthnAvailable() ? (
        <Button
          type="button"
          variant="outline"
          className="mt-3 w-full"
          onClick={() => passkey.mutate()}
          disabled={passkey.isPending}
        >
          <KeyRound aria-hidden className="size-4" />
          Sign in with a passkey
        </Button>
      ) : null}
    </AuthCard>
  );
}
